import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { IJoistProfileType } from "building-types/framing/iJoistTypes";
import type { LumberProfileType } from "building-types/framing/lumberTypes";

export enum FramingComponentType {
  DIMENSIONAL_LUMBER = "DIMENSIONAL_LUMBER",
  COMPOSITE_LUMBER = "COMPOSITE_LUMBER",
  I_JOIST = "I_JOIST",
  TRUSS = "TRUSS" /**Truss has no chord/web detail */,
  CUSTOM = "CUSTOM",
}

export const framingComponentTypeLabels: { readonly [key in FramingComponentType]: string } = {
  [FramingComponentType.DIMENSIONAL_LUMBER]: "Dimensional Lumber",
  [FramingComponentType.COMPOSITE_LUMBER]: "Composite Lumber",
  [FramingComponentType.I_JOIST]: "I-Joist",
  [FramingComponentType.TRUSS]: "Truss",
  [FramingComponentType.CUSTOM]: "Custom",
};

export interface FramingComponentBase<T extends FramingComponentType> extends DocEntityBase<"FRAMING_COMPONENT"> {
  readonly id: PrimaryEntityId<"FRAMING_COMPONENT">;
  readonly name: string;
  readonly type: T;
}

export interface DimensionalLumberComponent extends FramingComponentBase<FramingComponentType.DIMENSIONAL_LUMBER> {
  readonly lumberProfileType: LumberProfileType;
}

export interface CompositeLumberComponent extends FramingComponentBase<FramingComponentType.COMPOSITE_LUMBER> {
  readonly depth: number;
  readonly width: number;
}

// "IJoist" is a structural-product proper noun (engineered wood I-joist), not an
// interface-prefix Hungarian notation, so the `^I[A-Z]` interface-name guard does
// not apply meaningfully here.
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IJoistComponent extends FramingComponentBase<FramingComponentType.I_JOIST> {
  readonly iJoistProfileType: IJoistProfileType;
}

export interface TrussComponent extends FramingComponentBase<FramingComponentType.TRUSS> {
  readonly depth: number;
  readonly width: number;
}

export interface CustomFramingComponent extends FramingComponentBase<FramingComponentType.CUSTOM> {
  readonly depth: number;
  readonly width: number;
}

export type FramingComponentEntity =
  | DimensionalLumberComponent
  | CompositeLumberComponent
  | IJoistComponent
  | TrussComponent
  | CustomFramingComponent;

export type FramingComponentId =
  | DimensionalLumberComponent["id"]
  | CompositeLumberComponent["id"]
  | IJoistComponent["id"]
  | TrussComponent["id"]
  | CustomFramingComponent["id"];

export type FramingComponentEntityTable = {
  [id: FramingComponentId]: FramingComponentEntity;
};

/**
 * This list defines the components that are eligible to be used as a rim board.
 */
export const RIM_BOARD_ELIGIBLE_TYPES: ReadonlySet<FramingComponentType> = new Set([
  FramingComponentType.DIMENSIONAL_LUMBER,
  FramingComponentType.COMPOSITE_LUMBER,
  FramingComponentType.CUSTOM,
]);
